/**
 * The Platform Analytics report model — ONE implementation, two surfaces.
 *
 * Authenticated Platform Analytics (/platform-analytics) and the public
 * report microsite (/report/:publicReportToken) render the same numbers
 * because they call the functions in this file on the same canonical payload.
 * Metric selection, the measurement-only exclusion, the governed Meta rollup,
 * zero-row suppression, prior-period deltas, window arithmetic and number
 * formatting all live here and nowhere else.
 *
 * If a metric needs to change, it changes once. A second copy of any of this
 * is how the two surfaces would start disagreeing, which is exactly what the
 * parity tests in both repos exist to prevent.
 *
 * Everything here is pure: no fetch, no storage, no router, no clock beyond
 * the reference date a caller passes in. That is what makes both surfaces
 * testable against the same fixtures.
 */

import {
  resolvePlatformBranding,
  resolvePlatformBrandKey,
} from "../utils/platformBranding";
import {
  formatFreshnessMinutes,
  resolvePlatformFreshness,
  resolveTrendDirection,
} from "./PlatformContributionCard";
import {
  COMPLETED_VIEWS_UNREPORTED_NOTE,
  COST_PER_COMPLETED_VIEW_KEY,
  COST_PER_COMPLETED_VIEW_LABEL,
  COST_PER_COMPLETED_VIEW_LONG_LABEL,
  describeCostPerCompletedView,
  formatCostPerCompletedView,
  resolveOutcomeMetricKey,
} from "./costPerCompletedView";
import {
  campaignKpiScopeIsSinglePlatform,
  resolveCampaignKpiScope,
} from "./campaignKpiScope";
import { resolveFocusedKpiMetrics } from "./focusedPlatformKpiProfile";
import { addCalendarDays, reportingToday } from "../utils/reportingCalendar";
import {
  COST_PER_QUALIFIED_VIDEO_VIEW_LONG_LABEL,
  aggregateQualifiedVideoViews,
} from "./metricBasis";
import {
  resolveSpendEfficiencyContract,
  resolveSpendEfficiencyMetric,
  resolveSpendEfficiencySpend,
} from "./spendEfficiencyContract";
/* `roundHalfUp` lives with the other formatters and is re-exported below, so
   both this model and the shared metric formatter round one way. */
import { formatPlatformMetricValue, roundHalfUp } from "./platformAnalyticsProfiles";
import {
  QUALIFIED_VIEWS_UNREPORTED_NOTE,
  buildCostPerQualifiedVideoViewTile,
  buildQualifiedVideoKpiTiles,
} from "./qualifiedVideoPresentation";
import { buildTimeSeriesDeliveryChartModel } from "./deliveryChartModel";
import { indexObservedSiteVisitsByDate } from "./observedSiteVisits";
import {
  resolveOutcomeSemantics,
  resolveOutcomeConversionsPresentation,
} from "./outcomeSemantics";
import {
  aggregateEligibleRateBasis,
  readPlatformCounts,
  resolveCompletionRatePercent,
  resolvePlatformEfficiencyMetric,
  resolvePlatformRateParticipation,
} from "./platformEfficiencyMetric";

export const PLATFORM_ANALYTICS_DATE_PRESETS = Object.freeze([
  { key: "last_7d", label: "Last 7 days", days: 7 },
  { key: "last_14d", label: "Last 14 days", days: 14 },
  { key: "last_30d", label: "Last 30 days", days: 30 },
  { key: "last_90d", label: "Last 90 days", days: 90 },
]);

export const PLATFORM_ANALYTICS_METRICS = Object.freeze([
  { key: "spend", label: "Spend", kind: "currency" },
  { key: "impressions", label: "Impressions", kind: "count" },
  { key: "clicks", label: "Clicks", kind: "count" },
]);

// Measurement-only channels are never delivery platforms and must not enter
// platform legends, rails, or aggregates on either surface.
export const MEASUREMENT_ONLY_KEYS = Object.freeze(
  new Set(["google_analytics", "ga4", "site_analytics", "web_analytics", "shopify"])
);

// Meta-family identities render as one governed Meta platform when the
// canonical metaRollup is present, with sub-channels disclosed explicitly.
export const META_FAMILY_BRAND_KEYS = Object.freeze(
  new Set(["meta", "facebook", "instagram", "messenger", "audience_network"])
);

export const META_SUB_CHANNEL_LABELS = Object.freeze({
  facebook: "Facebook",
  instagram: "Instagram",
  audience_network: "Audience Network",
  messenger: "Messenger",
  meta: "Meta",
});

/*
 * What a focused strip says about a figure canonical reporting only carries
 * for the whole window. Named once because the model states it and the KPI
 * slot repeats it.
 */
export const AGGREGATE_ONLY_NOTE = "Whole-window figure — not scoped to one platform";

export { roundHalfUp };

/* An exact money figure for help text — never the compact card face. Four
   decimals so a sub-cent rate stays reconcilable against a spend report. */
const currencyExact = (value) =>
  `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;

export const toFiniteOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const isoDay = (date) => date.toISOString().slice(0, 10);

export const isIsoDayString = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

export const buildPresetWindow = (presetKey, reference = new Date()) => {
  const preset =
    PLATFORM_ANALYTICS_DATE_PRESETS.find((entry) => entry.key === presetKey) ||
    PLATFORM_ANALYTICS_DATE_PRESETS[2];
  // Calendar days ending on the Eastern reporting day (not the UTC date), with
  // the same inclusive span and an equal-length prior window before it.
  const endDate = reportingToday(reference);
  const startDate = addCalendarDays(endDate, -(preset.days - 1));
  const priorEndDate = addCalendarDays(startDate, -1);
  return {
    preset,
    startDate,
    endDate,
    priorStartDate: addCalendarDays(priorEndDate, -(preset.days - 1)),
    priorEndDate,
  };
};

export const buildExplicitWindow = (startDate, endDate) => {
  const msPerDay = 86400000;
  const startMs = Date.parse(`${startDate}T00:00:00Z`);
  const endMs = Date.parse(`${endDate}T00:00:00Z`);
  const days = Math.round((endMs - startMs) / msPerDay) + 1;
  const priorEnd = new Date(startMs - msPerDay);
  const priorStart = new Date(startMs - days * msPerDay);
  return {
    preset: null,
    days,
    startDate,
    endDate,
    priorStartDate: isoDay(priorStart),
    priorEndDate: isoDay(priorEnd),
  };
};

export const pickSpend = (metrics = {}) =>
  toFiniteOrNull(
    metrics.clientFacingSpend ??
      metrics.spendClientFacing ??
      metrics.billedSpend ??
      metrics.spend ??
      metrics.spendToDate
  );

export const pickMetric = (metrics = {}, key) =>
  key === "spend" ? pickSpend(metrics) : reportedMetricCount(metrics?.[key]);

/*
 * A count as REPORTED, or null.
 *
 * Deliberately not `toFiniteOrNull`: `Number(null)` is 0, so reading a count
 * through it turns an explicit absence into a reported zero. That is not
 * hypothetical here — the canonical layer emits `video_completed: null` when a
 * source carries no completion column, and that null sat at the END of the
 * alias chain below, so it was the value that survived and became 0. A channel
 * that reported nothing showed "0 Completed Views" instead of the canonical
 * unavailable mark, and bought its way into rate denominators it had no
 * observation for.
 */
const reportedMetricCount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

// Canonical carries video views camelCased; the welcome-sourced fallback tier
// serializes the same observation snake_cased. Both are the same number.
export const pickVideoViews = (metrics = {}) =>
  reportedMetricCount(
    metrics?.trueViewViews ?? metrics?.true_view_views ?? metrics?.videoViews ?? metrics?.video_views
  );

// The provider-native YouTube count. Unlike pickVideoViews this does not use
// a generic fallback; callers decide when legacy compatibility is allowed.
export const pickTrueViewViews = (metrics = {}) =>
  reportedMetricCount(metrics?.trueViewViews ?? metrics?.true_view_views);

// Completed views, in every spelling the canonical layer and the public
// projection emit. StackAdapt's `vcomp_95` reaches here already canonicalized
// as `videoCompletions`; the snake_cased forms are the welcome-sourced and
// legacy serializations of the same observation.
export const pickVideoCompletions = (metrics = {}) =>
  reportedMetricCount(
    metrics?.videoCompletions ?? metrics?.video_completions ?? metrics?.video_completed
  );

/*
 * Video STARTS — plays the platform reported as initiated.
 *
 * Read under their own name, never off `videoViews`, and the distinction is
 * not pedantic. On CTV the canonical layer aliases the provider's start count
 * into `video_views` (the channel-grain delivery query never asks for a views
 * field at all), so the two are the same number there — and reading starts off
 * the views field would work, right up until a source that reports both
 * arrives and the strip starts calling real views "starts". A view is counted
 * at whatever early threshold the platform chose; a start is a play that
 * began. They are separate readings and are read separately.
 */
export const pickVideoStarts = (metrics = {}) =>
  reportedMetricCount(
    metrics?.videoStarts ??
      metrics?.video_started ??
      metrics?.videoStarted ??
      metrics?.video_starts
  );

/*
 * The completion rate the SOURCE stated, if it stated one.
 *
 * Preferred over anything derived here. The canonical layer computes this
 * starts-pinned and eligibility-scoped, and a page that re-divides the totals
 * beside it is a second implementation of a governed rate — which is how two
 * surfaces start disagreeing about one number. Local derivation is the
 * fallback, not the rule.
 */
export const pickCompletionRate = (metrics = {}) =>
  reportedMetricCount(
    metrics?.completionRate ?? metrics?.videoCompletionRate ?? metrics?.video_completion_rate
  );

/**
 * A percentage derived from two canonical counts, or null.
 *
 * Null is the whole point. A rate is only meaningful when its denominator is a
 * real, positive, reported observation — so a missing, zero, or non-numeric
 * denominator yields null rather than 0, and the surfaces render the canonical
 * unavailable mark. Nothing here invents a numerator: an absent count stays
 * absent instead of being read as 0.
 */
export const deriveRatePercent = (numerator, denominator) => {
  // Not toFiniteOrNull: Number(null) is 0, and reading an absent count as a
  // reported zero is precisely the mistake this function exists to avoid.
  const reported = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const value = reported(numerator);
  const basis = reported(denominator);
  if (value == null || basis == null || basis <= 0 || value < 0) return null;
  return (value / basis) * 100;
};

/**
 * The KPI strip's aggregate efficiency rates, each computed ONLY across the
 * platforms eligible for that rate — SUM(numerator over eligible platforms) /
 * SUM(impressions over those SAME platforms), through the one governed
 * participation resolver the cards and tooltips read.
 *
 * This replaced a blended derivation over the strip's own whole-scope totals,
 * which was statistically wrong in both directions on a mixed flight: CTV
 * impressions (inventory with no click to offer) diluted CTR toward a number
 * no platform reported, and Display impressions (inventory with no video-view
 * contract) diluted the video view rate the same way. The counts the strip
 * shows are still the whole reporting scope; the rates state what the
 * ELIGIBLE portion of that scope actually did, and `rateBasis` names exactly
 * which platforms each rate divides.
 */
export const buildEligibleAnalyticsRateBasis = (rows = []) =>
  aggregateEligibleRateBasis(
    (Array.isArray(rows) ? rows : []).map((row) => ({
      platformKey: row?.key ?? row?.platformKey ?? null,
      /*
       * The source that measured the row, forwarded so the video view rate
       * can tell "two platforms, one measurement" from "two platforms, two
       * different definitions of a view". Absent, it falls back to the
       * platform key, which declines the blend rather than assuming it —
       * the same default the canonical metricBasis engine applies.
       */
      sourceKey: row?.sourceLabel ?? row?.sourceKey ?? row?.source ?? null,
      ...readPlatformCounts(row || {}),
    }))
  );

/*
 * The campaign-level qualified-video basis for this page.
 *
 * One engine, one contract. `aggregateQualifiedVideoViews` is the same
 * function Campaign Insights and the backend rollup use, so Platform
 * Analytics cannot arrive at a different answer from the same delivery —
 * which is the failure this whole metric was created to end, one surface over.
 *
 * The canonical scalar wins for the total wherever the payload carries one:
 * the server is the authority, and a page that quietly preferred its own sum
 * would be a second implementation no matter how identical the arithmetic
 * looked today. `reconciles` is false when the two disagree, so a divergence
 * is a visible fact rather than a silent overwrite.
 */
function buildQualifiedVideoModel(rows, aggregate = {}) {
  const basis = aggregateQualifiedVideoViews(rows || []);
  if (!basis || basis.value === null) return basis || null;

  const canonicalTotal = Number(
    aggregate?.qualifiedVideoViews ?? aggregate?.qualified_video_views
  );
  const hasCanonicalTotal = Number.isFinite(canonicalTotal);

  return {
    ...basis,
    value: hasCanonicalTotal ? canonicalTotal : basis.value,
    derivedValue: basis.value,
    canonicalValue: hasCanonicalTotal ? canonicalTotal : null,
    reconciles: hasCanonicalTotal ? canonicalTotal === basis.value : null,
  };
}

/**
 * What that qualified video cost, on the same terms.
 *
 * Same shape as the total above and for the same reason: the canonical scalar
 * is the authority when the payload carries one, the page's own governed fold
 * is the fallback, and `reconciles` records whether they agree instead of one
 * silently overwriting the other.
 *
 * The fold is NOT a second methodology. `aggregateCostPerQualifiedVideoView`
 * is the same engine function the backend rollup calls; what happens here is
 * that a page which already holds the rows can state the metric during the
 * window between this shipping and a backend that publishes it, rather than
 * showing an unavailable card for a value it demonstrably has.
 */
function buildCostPerQualifiedVideoViewModel(spendEfficiency) {
  return resolveSpendEfficiencyMetric(spendEfficiency, { metricKey: "cpqv" });
}

export const summaryChannelEntries = (summary) => {
  const reporting = summary?.canonicalReporting || summary?.canonicalSummary || summary || {};
  const byChannel = reporting.byChannel || reporting.channelBreakdown || {};
  return Object.entries(byChannel && typeof byChannel === "object" ? byChannel : {});
};

const FRESHNESS_BASES = new Set([
  "SOURCE_REPORTED",
  "PROVIDER_OBSERVED",
  "INGESTED",
  "UNAVAILABLE",
]);

const normalizeFreshnessEvidence = (freshness = null) => {
  if (!freshness || typeof freshness !== "object") return null;
  const timestamp =
    freshness.timestamp || freshness.sourceUpdatedAt || freshness.sourceTimestamp || null;
  return {
    timestamp,
    basis: FRESHNESS_BASES.has(freshness.basis)
      ? freshness.basis
      : timestamp
      ? null
      : "UNAVAILABLE",
    sourceReportedAt: freshness.sourceReportedAt || null,
    observedAt: freshness.observedAt || null,
    ingestedAt: freshness.ingestedAt || null,
    complete: freshness.complete !== false && Boolean(timestamp),
    contributors: Array.isArray(freshness.contributors) ? freshness.contributors : [],
    missingContributors: Array.isArray(freshness.missingContributors)
      ? freshness.missingContributors
      : [],
    failureReason: freshness.failureReason || null,
    isLive: freshness.isLive === true,
    isFallback: freshness.isFallback === true,
    platforms:
      freshness.platforms && typeof freshness.platforms === "object"
        ? freshness.platforms
        : {},
  };
};

const contributorMatchesPlatform = (contributor, platformKey) => {
  const contributorKey = resolvePlatformBrandKey(contributor?.platform) ||
    String(contributor?.platform || "").toLowerCase();
  if (platformKey === "meta") return META_FAMILY_BRAND_KEYS.has(contributorKey) || contributorKey === "meta";
  return contributorKey === platformKey;
};

const freshnessForPlatform = (freshness, platformKey, legacyTimestamp = null) => {
  const root = normalizeFreshnessEvidence(freshness);
  const contributors = (root?.contributors || [])
    .filter((contributor) => contributorMatchesPlatform(contributor, platformKey));
  const explicitPlatformFreshness = normalizeFreshnessEvidence(
    root?.platforms?.[platformKey]
  );
  if (explicitPlatformFreshness) return explicitPlatformFreshness;
  if (!contributors.length) {
    return legacyTimestamp
      ? normalizeFreshnessEvidence({ timestamp: legacyTimestamp, complete: true })
      : null;
  }
  const sorted = contributors
    .filter((contributor) => contributor?.timestamp)
    .sort((left, right) => String(left.timestamp).localeCompare(String(right.timestamp)));
  const oldest = sorted[0] || null;
  return normalizeFreshnessEvidence({
    ...(oldest || {}),
    timestamp: oldest?.timestamp || null,
    complete:
      contributors.length > 0 &&
      contributors.every((contributor) => contributor?.complete === true),
    contributors,
    missingContributors: contributors
      .filter((contributor) => contributor?.complete !== true)
      .map((contributor) => ({
        platform: contributor.platform || platformKey,
        sourceLabel: contributor.sourceLabel || contributor.source || null,
      })),
  });
};

/**
 * Build the page model from a canonical summary response. Pure so tests can
 * exercise every state without a network, and so the authenticated page and
 * the public microsite provably derive the same model from the same payload.
 */
export const buildPlatformAnalyticsModel = (summary, priorSummary = null) => {
  const reporting = summary?.canonicalReporting || summary?.canonicalSummary || summary || {};
  const spendEfficiency = resolveSpendEfficiencyContract(reporting);
  const canonicalFreshness = reporting.dataFreshness || null;
  const rowsByPlatform = new Map();
  const unavailableSpend = new Set();
  const unavailableImpressions = new Set();
  const unavailableClicks = new Set();

  // Governed Meta rollup: when the canonical response discloses a Meta
  // rollup, every Meta-family channel renders as one "Meta" platform whose
  // sub-channel composition is shown explicitly — matching the Dashboard
  // preview's Meta bucket so both surfaces resolve the same identity.
  const metaRollup =
    reporting.metaRollup && typeof reporting.metaRollup === "object"
      ? reporting.metaRollup
      : null;

  summaryChannelEntries(summary).forEach(([channelKey, entry]) => {
    const brandKey = resolvePlatformBrandKey(entry?.platform || entry?.channel || channelKey) ||
      String(channelKey || "").toLowerCase();
    if (MEASUREMENT_ONLY_KEYS.has(String(channelKey).toLowerCase())) return;
    if (MEASUREMENT_ONLY_KEYS.has(String(brandKey).toLowerCase())) return;
    if (metaRollup && META_FAMILY_BRAND_KEYS.has(String(brandKey).toLowerCase())) return;
    const metrics = entry?.metrics || entry || {};
    const existing = rowsByPlatform.get(brandKey) || {
      key: brandKey,
      spend: 0,
      impressions: 0,
      // null, not 0: a platform whose payload carries no click or
      // video-view observation has not reported zero — the eligibility
      // layer must tell an unreported count apart from a reported zero.
      clicks: null,
      videoViews: null,
      trueViewViews: null,
      videoStarts: null,
      videoCompletions: null,
      // The source's own completion rate, kept unsummed: a rate is not
      // additive, so when one brand key folds several canonical channels the
      // last stated rate is meaningless and the strip derives from the counts
      // instead. Single-channel rows — every focused CTV view — keep it.
      completionRate: null,
      completionRateChannels: 0,
      /*
       * The platform's OWN outcome count, and the two cost rates the canonical
       * layer already computed for it.
       *
       * All three were being dropped on the floor. `byChannel[display]` has
       * carried `conversions` for as long as the outcome-semantics verifier
       * has read it — that verifier's whole job is to check every
       * conversions-bearing CHANNEL is StackAdapt-sourced, which it could not
       * do if the field were aggregate-only — yet this model read conversions
       * exclusively off `aggregateTotals`. So a focused strip had no
       * platform-scoped figure to show and printed `—` under a note claiming
       * the number was "not scoped to one platform", when the correctly scoped
       * number was sitting in the payload it had just parsed.
       *
       * `cpc`/`cpm` are read, never derived: the canonical layer computes them
       * from client-billed spend, and a page that re-divided its own totals
       * beside them would be a second rate producer — the way two surfaces
       * start disagreeing about one number.
       *
       * null, not 0, on every one of them: an absent outcome is not a reported
       * zero, and only a real observation may render as `0`.
       */
      conversions: null,
      cpc: null,
      cpm: null,
      /*
       * Which source measured this platform. Carried so the video view rate
       * can tell one measurement spanning two platforms from two different
       * definitions of a view; null leaves the rate declining to blend.
       */
      sourceLabel: null,
      hasAny: false,
      lastFetchedAt: null,
      dataFreshness: null,
    };
    if (existing.sourceLabel == null) {
      const sourceLabel = entry?.sourceLabel ?? metrics?.sourceLabel ?? entry?.source ?? null;
      existing.sourceLabel = sourceLabel == null ? null : String(sourceLabel);
    }
    for (const metric of ["spend", "impressions"]) {
      const contractedSpend = spendEfficiency?.byPlatform?.[brandKey]
        ? resolveSpendEfficiencySpend(spendEfficiency, brandKey)
        : null;
      const value =
        metric === "spend" && spendEfficiency?.byPlatform?.[brandKey]
          ? contractedSpend
          : pickMetric(metrics, metric);
      // A delivered row with no billed-spend evidence cannot become a zero
      // or a partial sum when aliases fold into the same platform.
      if (metric === "spend" && value == null) unavailableSpend.add(brandKey);
      if (metric === "impressions" && value == null) unavailableImpressions.add(brandKey);
      if (value != null && value > 0) {
        existing[metric] += value;
        existing.hasAny = true;
      }
    }
    // A REPORTED zero clicks is kept as 0 (a real observation that divides
    // the platform's CTR); an absent count stays null. Zero-row suppression
    // is unchanged: only positive counts make a row exist.
    const rowClicks = pickMetric(metrics, "clicks");
    if (rowClicks == null) unavailableClicks.add(brandKey);
    if (rowClicks != null) {
      existing.clicks = (existing.clicks ?? 0) + rowClicks;
      if (rowClicks > 0) existing.hasAny = true;
    }
    // Video views never make a row exist. Zero-row suppression stays exactly
    // where it was — spend, impressions and clicks decide what renders. A
    // REPORTED zero is kept as 0 (a real observation that participates in the
    // platform's video view rate) while an absent count stays null — the
    // eligibility layer needs to tell those two states apart.
    const rowTrueViewViews = pickTrueViewViews(metrics);
    const rowVideoViews = rowTrueViewViews ?? pickVideoViews(metrics);
    if (rowVideoViews != null) {
      existing.videoViews = (existing.videoViews ?? 0) + rowVideoViews;
      if (brandKey === "youtube") {
        existing.trueViewViews = (existing.trueViewViews ?? 0) + rowVideoViews;
      }
    }
    // Completed views, on exactly the same terms as video views: they never
    // make a row exist, a REPORTED zero is kept as 0, and an absent count
    // stays null. CPCV depends on telling those apart — a channel whose
    // source carries no completion column must reach the tile as "not
    // reported", not as a zero that would look like a divide-by-zero.
    const rowVideoCompletions = pickVideoCompletions(metrics);
    if (rowVideoCompletions != null) {
      existing.videoCompletions = (existing.videoCompletions ?? 0) + rowVideoCompletions;
    }
    // Video starts, on exactly the same terms as the two counts above: they
    // never make a row exist, a REPORTED zero is kept as 0, and an absent
    // count stays null so the completion rate can refuse to divide by it.
    const rowVideoStarts = pickVideoStarts(metrics);
    if (rowVideoStarts != null) {
      existing.videoStarts = (existing.videoStarts ?? 0) + rowVideoStarts;
    }
    const rowCompletionRate = pickCompletionRate(metrics);
    if (rowCompletionRate != null) {
      existing.completionRate = rowCompletionRate;
      existing.completionRateChannels += 1;
    }
    /*
     * The platform's outcome count. A REPORTED zero is kept as 0 — a real
     * observation that the card must render as `0`, not as `—` — while an
     * absent count stays null. Counts never make a row exist: zero-row
     * suppression stays exactly where it was, on spend, impressions and clicks.
     */
    const rowConversions = reportedMetricCount(metrics?.conversions);
    if (rowConversions != null) {
      existing.conversions = (existing.conversions ?? 0) + rowConversions;
    }
    /*
     * The canonical cost rates, taken as stated. NOT summed: a rate is not
     * additive, so where one brand key folds several canonical channels the
     * last stated rate would be meaningless. The focused strip derives from the
     * folded counts in exactly that case — see `buildFocusedPlatformKpiStrip`,
     * which counts the stating channels for the same reason `completionRate`
     * does above.
     */
    const rowCpc = reportedMetricCount(metrics?.cpc);
    if (rowCpc != null) {
      existing.cpc = rowCpc;
      existing.cpcChannels = (existing.cpcChannels || 0) + 1;
    }
    const rowCpm = reportedMetricCount(metrics?.cpm);
    if (rowCpm != null) {
      existing.cpm = rowCpm;
      existing.cpmChannels = (existing.cpmChannels || 0) + 1;
    }
    const fetched = entry?.sourceMetadata?.lastFetchedAt || entry?.lastFetchedAt || null;
    if (fetched && (!existing.lastFetchedAt || fetched < existing.lastFetchedAt)) {
      existing.lastFetchedAt = fetched;
    }
    rowsByPlatform.set(brandKey, existing);
  });

  if (metaRollup) {
    const totals = metaRollup.totals || {};
    const metaRow = {
      key: "meta",
      spend: 0,
      impressions: 0,
      clicks: null,
      videoViews: null,
      hasAny: false,
      lastFetchedAt: null,
      subChannels: [],
    };
    for (const metric of ["spend", "impressions"]) {
      const value = pickMetric(totals, metric);
      if (value != null && value > 0) {
        metaRow[metric] = value;
        metaRow.hasAny = true;
      }
    }
    const metaClicks = pickMetric(totals, "clicks");
    if (metaClicks != null) {
      metaRow.clicks = metaClicks;
      if (metaClicks > 0) metaRow.hasAny = true;
    }
    const metaVideoViews = pickVideoViews(totals);
    if (metaVideoViews != null) metaRow.videoViews = metaVideoViews;
    metaRow.subChannels = Object.entries(metaRollup.subChannels || {})
      .map(([subKey, subMetrics]) => ({
        key: subKey,
        label: META_SUB_CHANNEL_LABELS[subKey] || subKey,
        spend: pickSpend(subMetrics) ?? 0,
        impressions: toFiniteOrNull(subMetrics?.impressions) ?? 0,
        clicks: toFiniteOrNull(subMetrics?.clicks) ?? 0,
      }))
      .sort((a, b) => (b.spend || 0) - (a.spend || 0));
    if (metaRow.hasAny) rowsByPlatform.set("meta", metaRow);
  }

  // Zero-only rows never create legends or rail cards.
  const rows = Array.from(rowsByPlatform.values())
    .filter((row) => row.hasAny)
    .map((row) => {
      const branding = resolvePlatformBranding(row.key) || {};
      const dataFreshness = freshnessForPlatform(
        canonicalFreshness,
        row.key,
        row.lastFetchedAt
      );
      return {
        ...row,
        spend: unavailableSpend.has(row.key) ? null : row.spend,
        impressions: unavailableImpressions.has(row.key) ? null : row.impressions,
        clicks: unavailableClicks.has(row.key) ? null : row.clicks,
        label: branding.label || row.key,
        dataFreshness,
        lastFetchedAt: dataFreshness?.timestamp || row.lastFetchedAt,
      };
    })
    .sort((a, b) => (b.spend || 0) - (a.spend || 0));

  const totals = { spend: 0, impressions: 0, clicks: 0 };
  rows.forEach((row) => {
    totals.spend += row.spend || 0;
    totals.impressions += row.impressions || 0;
    totals.clicks += row.clicks || 0;
  });
  if (rows.some((row) => row.spend == null)) totals.spend = null;
  if (rows.some((row) => row.impressions == null)) totals.impressions = null;
  if (rows.some((row) => row.clicks == null)) totals.clicks = null;

  const aggregate = reporting.aggregateTotals || reporting.metrics || {};
  const videoViews = pickVideoViews(aggregate);
  const conversions = toFiniteOrNull(aggregate.conversions);
  const reportedVideoViews = videoViews != null && videoViews > 0 ? videoViews : null;

  // The prior window, modelled exactly like this one. Built once and read
  // twice: for the window-level deltas the KPI strip states, and for the
  // per-platform prior rows the contribution cards compare against. The prior
  // payload carries the same `byChannel` breakdown as the current one, so a
  // platform's own prior efficiency is canonical evidence — not an
  // apportionment of a whole-window movement onto one platform.
  const priorModel = priorSummary ? buildPlatformAnalyticsModel(priorSummary, null) : null;
  const priorTotals = priorModel?.hasEvidence ? priorModel.totals : null;
  const priorRowsByPlatform = new Map(
    (priorModel?.hasEvidence ? priorModel.platforms : []).map((row) => [row.key, row])
  );

  /*
   * Per-platform derivations the contribution card presents.
   *
   * `ctr` is the same derivation the KPI strip uses — clicks over impressions
   * through the one canonical rate helper — so a card and the strip above it
   * can never disagree about what a rate means, and a platform that reported
   * no impressions gets null rather than a 0% it never claimed.
   *
   * `ctrDeltaPts` exists only when BOTH windows produced a real rate for THIS
   * platform. No prior request, a prior request that failed, a platform absent
   * from the prior window, or either window lacking a divisible denominator
   * all leave it null — and a null delta is what stops the card drawing a
   * direction arrow at all.
   *
   * `efficiencyDeltaPts` is the movement of the metric the platform is
   * ACTUALLY read on, which is not always CTR — see the card presentation
   * below. It exists only when both windows resolved to the SAME metric: a
   * platform that reported video views last window and none this one has not
   * moved from 99% to 0.05%, it has changed what it can be read on at all, and
   * an arrow across that boundary would state a comparison nobody made.
   */
  const platforms = rows.map((row) => {
    /*
     * A row's CTR exists only when the platform is CTR-ELIGIBLE — capability
     * plus reported click evidence, the same governed rule the aggregates
     * use. A video-first platform's zero-filled clicks never mint a live
     * 0.00% CTR onto the model object for some consumer to print.
     */
    const rowParticipation = resolvePlatformRateParticipation({
      platformKey: row.key,
      ...readPlatformCounts(row),
    });
    const ctr = rowParticipation.ctr.eligible
      ? deriveRatePercent(row.clicks, row.impressions)
      : null;
    const priorRow = priorRowsByPlatform.get(row.key) || null;
    const priorParticipation = priorRow
      ? resolvePlatformRateParticipation({
          platformKey: row.key,
          ...readPlatformCounts(priorRow),
        })
      : null;
    const priorCtr =
      priorRow && priorParticipation?.ctr?.eligible
        ? deriveRatePercent(priorRow.clicks, priorRow.impressions)
        : null;
    const efficiency = resolvePlatformEfficiencyMetric({
      platformKey: row.key,
      ...readPlatformCounts(row),
    });
    const priorEfficiency = priorRow
      ? resolvePlatformEfficiencyMetric({
          platformKey: row.key,
          ...readPlatformCounts(priorRow),
        })
      : null;
    const comparable =
      efficiency != null && priorEfficiency != null && efficiency.key === priorEfficiency.key;
    return {
      ...row,
      spendShare: row.spend != null && totals.spend > 0 ? (row.spend / totals.spend) * 100 : null,
      ctr,
      priorCtr,
      ctrDeltaPts: ctr != null && priorCtr != null ? ctr - priorCtr : null,
      efficiencyDeltaPts: comparable ? efficiency.value - priorEfficiency.value : null,
    };
  });

  const deltas = {};
  if (priorTotals) {
    for (const metric of ["spend", "impressions", "clicks"]) {
      const current = totals[metric];
      const prior = priorTotals[metric];
      if (Number.isFinite(current) && Number.isFinite(prior) && prior > 0) {
        deltas[metric] = ((current - prior) / prior) * 100;
      }
    }
  }

  const freshness = normalizeFreshnessEvidence(reporting.dataFreshness) || {};
  const oldestPlatform = platforms
    .filter((row) => row.lastFetchedAt)
    .sort((a, b) => String(a.lastFetchedAt).localeCompare(String(b.lastFetchedAt)))[0];

  const rateBasis = buildEligibleAnalyticsRateBasis(rows);

  return {
    platforms,
    totals,
    deltas,
    rates: {
      ctr: rateBasis.ctr.value,
      videoViewRate: rateBasis.vvr.value,
    },
    // Which platforms each aggregate rate actually divides — the governed
    // scope disclosure, and what the daily-reconciliation tests read.
    rateBasis,
    spendEfficiency,
    spendBasis: spendEfficiency?.spendBasis || null,
    spendBasisLabel: spendEfficiency?.spendBasisLabel || null,
    hasEvidence: platforms.length > 0,
    /*
     * The CAMPAIGN-LEVEL video story, from the same governed engine Campaign
     * Insights reads.
     *
     * `videoViews` below stays exactly what it was — YouTube-compatible views
     * — because a focused platform page still states its own native metric,
     * and because nothing about the generic count became wrong. What was
     * wrong was letting it stand as the campaign's whole video story: on a
     * flight where CTV delivered 144,166 completed views, a cross-platform
     * strip reading 29.3K invites exactly the conclusion the client drew.
     */
    qualifiedVideo: buildQualifiedVideoModel(rows, aggregate),
    /* The price of the line above it, folded over exactly the platforms that
       produced it. Always present in the model — the KPI strip renders a card
       for it whether or not this window could state a value, because a strip
       that drops the card collapses to seven and re-opens the layout defect
       the qualified migration left behind. */
    costPerQualifiedVideoView: buildCostPerQualifiedVideoViewModel(spendEfficiency),
    videoViews: reportedVideoViews,
    conversions: conversions != null && conversions > 0 ? conversions : null,
    /*
     * The backend's ruling on what the conversions figure may be CALLED —
     * kept on the model so a focused rebuild names the same number the same
     * way. Absent or generic declarations render the generic label.
     */
    outcomeSemantics: resolveOutcomeSemantics(summary),
    /*
     * The ALL PLATFORMS strip, in the sole platform's vocabulary — present
     * only when this campaign's reporting scope resolves to exactly one
     * platform that declares a KPI profile, null otherwise.
     *
     * Built here rather than in the pages because both Cortex surfaces render
     * it and the shared decision has to be made once. A page reads it, it does
     * not re-decide it; and because it hangs off the model it survives the
     * focus projection unchanged, so drilling into the only platform a
     * one-platform campaign has cannot change which metrics are named.
     */
    campaignPlatformKpiStrip: buildCampaignPlatformKpiStrip({
      platforms,
      spendEfficiency,
      outcomeSemantics: resolveOutcomeSemantics(summary),
    }),
    // All-platforms scope keeps the conversions figure. A CPCV blended across
    // channels that do not share a completion definition is the manufactured
    // number the SEMANTIC classification exists to refuse — see
    // §resolveOutcomeMetricKey.
    outcomeKpi: buildOutcomeKpi({
      rows,
      scopeKey: null,
      windowConversions: conversions != null && conversions > 0 ? conversions : null,
      outcomeSemantics: resolveOutcomeSemantics(summary),
    }),
    freshnessAt: freshness.timestamp || null,
    dataFreshness: freshness,
    oldestPlatformLabel: oldestPlatform?.label || null,
    timeframe: reporting.timeframe || null,
    latestReportingWindow:
      summary?.latestReportingWindow &&
      isIsoDayString(summary.latestReportingWindow.start) &&
      isIsoDayString(summary.latestReportingWindow.end)
        ? {
            start: summary.latestReportingWindow.start,
            end: summary.latestReportingWindow.end,
          }
        : null,
  };
};

/*
 * The KPI strip's outcome slot — the tile between Video View Rate and Data
 * Freshness — as ONE decision both Cortex surfaces read.
 *
 * The authenticated page and the public report render the same strip from the
 * same model, so the choice of which metric occupies this position has to be
 * made in the model or the two will eventually disagree about it. Neither page
 * asks what platform it is looking at; it asks the model what goes here.
 *
 * `scopeKey` is the platform the strip is scoped to, or null for the
 * all-platforms view. Only a video-first scope resolves to CPCV — see
 * resolveOutcomeMetricKey for why a blended one deliberately does not.
 */
export const buildOutcomeKpi = ({
  rows = [],
  scopeKey = null,
  windowConversions = null,
  focused = false,
  outcomeSemantics = null,
  canonicalMetric = null,
} = {}) => {
  if (resolveOutcomeMetricKey(scopeKey) === COST_PER_COMPLETED_VIEW_KEY) {
    const cpcv = canonicalMetric;
    const value = Number.isFinite(cpcv?.value) ? cpcv.value : null;
    const unavailableNote = cpcv?.unavailableMessage || COMPLETED_VIEWS_UNREPORTED_NOTE;
    return {
      metricKey: COST_PER_COMPLETED_VIEW_KEY,
      label: COST_PER_COMPLETED_VIEW_LABEL,
      longLabel: COST_PER_COMPLETED_VIEW_LONG_LABEL,
      /* Carries the exact figure, so this tile and the Creative Performance
         table on the same page reconcile despite rounding differently. */
      definition: cpcv?.formula || describeCostPerCompletedView(value),
      value,
      display: formatCostPerCompletedView(value),
      /*
       * The supporting line always says something. With a value it expands the
       * acronym, because CPCV is new vocabulary on this strip and a reader
       * should not have to hover to learn it. Without one it names the missing
       * FACT — deliberately not `aggregateOnlyNote`, whose "not scoped to one
       * platform" is the opposite of what is true here: this reading IS scoped,
       * the completion count is simply absent from the source.
       */
      note: value != null ? COST_PER_COMPLETED_VIEW_LONG_LABEL : unavailableNote,
      spend: cpcv?.numerator ?? null,
      completedViews: cpcv?.denominator ?? null,
      reason: cpcv?.unavailableReason || null,
      spendBasis: cpcv?.spendBasis || null,
      spendBasisLabel: cpcv?.spendBasisLabel || null,
    };
  }
  /*
   * The conversions figure, under the backend's declared name.
   *
   * WHAT A FOCUSED SCOPE STATES, AND WHY THIS CHANGED
   *
   * A focused scope used to render `—` unconditionally and declare the figure
   * "not scoped to one platform". The reasoning was sound and the premise was
   * false: canonical reporting does NOT carry conversions only for the window.
   * `byChannel[<platform>].conversions` is a real per-platform observation —
   * the outcome-semantics verifier reads exactly that field to require every
   * conversions-bearing channel be StackAdapt-sourced — and on a live flight
   * the channels sum to the aggregate exactly. So the strip was printing an
   * unavailable mark, and a REASON that was factually wrong, over a correctly
   * scoped number it had been handed.
   *
   * A wrong `—` is not the safe direction. It reads as "this platform drove no
   * site visits", which is a stronger and more damaging claim than the
   * scope caveat it was trying to avoid making.
   *
   * The rules the fix keeps, because they are the ones that matter:
   *   - The platform's OWN reported count is the only thing that may be
   *     stated. A campaign-wide total is never apportioned, scaled, or
   *     attributed to a platform that did not report one — `rowConversions`
   *     comes from that platform's canonical row or it does not come at all.
   *   - Nothing is inferred from clicks, CTR, impressions, spend or the chart.
   *   - A reported 0 renders `0`; an ABSENT count renders `—` with a reason
   *     that says which fact is missing rather than mis-describing the scope.
   *
   * What the tile is CALLED remains the backend's ruling, resolved through the
   * shared outcome-semantics module: "Ad-Attributed Site Visits" only when the
   * live tracker configuration verified that every counted event is a
   * site-visit pixel fire, and the generic "Conversions" for every other — and
   * every absent — declaration. Focusing changes scope, never the name.
   */
  const presentation = resolveOutcomeConversionsPresentation(outcomeSemantics);
  const scopedConversions = focused
    ? (() => {
        const row = Array.isArray(rows) && rows.length === 1 ? rows[0] : null;
        const reported = row?.conversions;
        return reported === null || reported === undefined ? null : Number(reported);
      })()
    : windowConversions;
  const hasScopedValue = Number.isFinite(scopedConversions);

  return {
    metricKey: "conversions",
    label: presentation.label,
    longLabel: presentation.longLabel,
    definition: presentation.definition,
    outcomeClassification: presentation.classification,
    value: hasScopedValue ? scopedConversions : null,
    display: hasScopedValue
      ? formatAnalyticsValue(scopedConversions)
      : focused
      ? "—"
      : "Not reported",
    /*
     * The note names the missing FACT. Deliberately NOT `AGGREGATE_ONLY_NOTE`
     * any more: "not scoped to one platform" describes the old refusal, not
     * this platform's payload. When the count is genuinely absent the honest
     * statement is that this platform reported none — which tells a reader to
     * look at the source rather than at the campaign.
     */
    note: hasScopedValue
      ? null
      : focused
      ? "Not reported for this platform"
      : null,
    spend: null,
    completedViews: null,
    reason: null,
  };
};

/*
 * The focused strip for a platform that declares its own KPI profile.
 *
 * WHY THIS IS NOT EIGHT MORE TILES IN A COMPONENT
 *
 * Focusing Platform Analytics on CTV used to leave the generic strip in place,
 * so two of the eight primary positions went to Clicks (a zero-filled 0) and
 * CTR (a permanent `—`), and the three readings CTV is actually bought on had
 * nowhere to go. Worse than the waste: the "Video View Rate" card next to them
 * printed video views over IMPRESSIONS, while the dedicated CTV page printed
 * completions over STARTS under the name "Completion Rate". On NSC those are
 * 98.20% and 98.19% — the same 98.2% on screen, two different measurements, no
 * way for a reader to tell which one they were looking at.
 *
 * So the tiles are resolved from the platform's REGISTRY profile — the very
 * list the dedicated page renders — through the shared metric registry for
 * labels, units, help copy and formatting. Focused and dedicated are the same
 * eight cards because they are the same declaration, not because two files
 * were edited to match.
 *
 * WHAT IS DERIVED HERE, AND ON WHAT BASIS
 *
 * Only the two ratios the canonical row does not carry as a single field, and
 * both divide numbers already on this strip. CPM divides the Spend tile's own
 * value, and CPCV goes through the canonical engine over the same value, so
 * all three cost figures state one governed basis: a surface showing gross
 * Spend cannot show a net CPM or CPCV beside it, because there is one spend
 * number and it is divided, never re-priced. No margin arithmetic lives here
 * and none may.
 *
 * Every absent input yields `—`, never 0.
 */
export const buildFocusedPlatformKpiStrip = (
  row,
  { now = Date.now(), cpqvMetric = null, cpcvMetric = null, outcomeSemantics = null } = {}
) => {
  const metrics = resolveFocusedKpiMetrics(row?.key);
  if (!metrics) return null;

  /*
   * NOT `toFiniteOrNull`: `Number(null)` is 0, and reading an absent count as
   * a reported zero is precisely the mistake this strip exists to stop making.
   * A source that never sent a completion column must reach the tile as "not
   * reported", not as a 0 that reads like a delivery failure.
   */
  const reported = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const spend = reported(row?.spend);
  const impressions = reported(row?.impressions);
  const trueViewViews = reported(row?.trueViewViews);
  const videoStarts = reported(row?.videoStarts);
  const videoCompletions = reported(row?.videoCompletions);
  const cpcv = cpcvMetric;

  /*
   * The governed Completion Rate, from the one resolver that owns it.
   *
   * The precedence is unchanged — the source's stated rate wins for a
   * single-channel row, the counts answer otherwise — but it is no longer
   * spelled out here. The contribution legend and the platform card read CTV
   * on this same metric, and a rule written down twice is a rule that will
   * eventually be two rules.
   */
  const completionRate = resolveCompletionRatePercent({
    completionRate: row?.completionRate,
    completionRateChannels: row?.completionRateChannels,
    videoCompletions,
    videoStarts,
  });

  const clicks = reported(row?.clicks);

  /*
   * CTR from the two counts already on this strip, so the strip reconciles
   * against itself. Governed participation still decides whether the rate may
   * exist at all: click-less inventory contributes no rate rather than the
   * 0.00% its zero-filled field would compute.
   */
  const ctr = resolvePlatformRateParticipation({
    platformKey: row?.key,
    ...readPlatformCounts(row || {}),
  }).ctr.eligible
    ? deriveRatePercent(clicks, impressions)
    : null;

  /*
   * THE TWO COST RATES ARE READ, NOT RE-DERIVED — AND THE BASIS IS WHY.
   *
   * The canonical layer computes cpc and cpm from CLIENT-BILLED (gross) spend
   * and publishes them per channel. Taking the published figure keeps one
   * governed producer for the metric, and — the part that actually matters —
   * makes it impossible for this strip to divide a spend value of a different
   * basis than the Spend card beside it. There is one spend number on this
   * surface and it is gross.
   *
   * The local division is a FALLBACK for the one case the published rate
   * cannot cover: a brand key that folds several canonical channels, where a
   * single stated rate describes only the last channel folded. It divides
   * `spend` — the same gross value the Spend card renders, summed from the
   * same rows — so the fallback is single-basis by construction too. It is
   * NOT a licence to re-price: no margin arithmetic lives here and none may.
   */
  const foldedChannels = (count) => Number(count || 0) > 1;
  const cpc =
    row?.cpc != null && !foldedChannels(row?.cpcChannels)
      ? reported(row.cpc)
      : spend != null && clicks != null && clicks > 0
      ? spend / clicks
      : null;
  const cpm =
    row?.cpm != null && !foldedChannels(row?.cpmChannels)
      ? reported(row.cpm)
      : spend != null && impressions != null && impressions > 0
      ? (spend / impressions) * 1000
      : null;

  /*
   * The platform's OWN outcome count. Read straight off the canonical row —
   * never the campaign total, never apportioned, never inferred from the
   * clicks or impressions sitting beside it. A reported 0 survives as 0.
   */
  const outcome = reported(row?.conversions);
  const outcomePresentation = resolveOutcomeConversionsPresentation(outcomeSemantics);

  /*
   * The arithmetic behind each cost card, stated in its help text.
   *
   * A compact card renders "$0.61", and a reader who needs to reconcile that
   * against a spend report needs the unrounded figure and both operands — plus
   * the SPEND BASIS, which is the fact that makes the number comparable to
   * anything at all. Client-billed is named explicitly rather than implied,
   * because the one way this card could be wrong without looking wrong is by
   * dividing a net working-media cost under a gross label.
   */
  const describeCost = (value, denominator, denominatorLabel, scaleNote = "") => {
    if (value == null || spend == null || denominator == null) return null;
    return `Client-billed (gross) spend ${currencyExact(spend)} ÷ ${denominator.toLocaleString(
      "en-US"
    )} ${denominatorLabel}${scaleNote} = ${currencyExact(value)}. Rounded for display only.`;
  };

  const platformFreshness =
    normalizeFreshnessEvidence(row?.dataFreshness) ||
    normalizeFreshnessEvidence(
      row?.lastFetchedAt ? { timestamp: row.lastFetchedAt, complete: true } : null
    );
  const freshnessMinutes = (() => {
    const then = platformFreshness?.timestamp
      ? new Date(platformFreshness.timestamp).getTime()
      : NaN;
    return Number.isFinite(then) ? Math.max(0, (now - then) / 60000) : null;
  })();

  const values = {
    spend,
    impressions,
    clicks,
    ctr,
    cpc,
    cpm,
    trueViewViews,
    videoStarts,
    videoCompletions,
    youtubeVideoCompletions: videoCompletions,
    vcr: completionRate,
    cpqv: Number.isFinite(cpqvMetric?.value) ? cpqvMetric.value : null,
    cpcv: Number.isFinite(cpcv?.value) ? cpcv.value : null,
    outcome,
    dataFreshness: freshnessMinutes,
  };

  /*
   * When a figure is missing the card says WHICH FACT is missing, not that the
   * window was quiet — a reader who sees `—` needs to know whether to look
   * upstream at the source or at the campaign.
   */
  const unavailableNotes = {
    cpqv: cpqvMetric?.unavailableMessage || QUALIFIED_VIEWS_UNREPORTED_NOTE,
    cpcv: cpcv?.unavailableMessage || COMPLETED_VIEWS_UNREPORTED_NOTE,
    vcr: "Completion rate not reported",
    videoStarts: "Video starts not reported",
    videoCompletions: COMPLETED_VIEWS_UNREPORTED_NOTE,
    trueViewViews: QUALIFIED_VIEWS_UNREPORTED_NOTE,
    youtubeVideoCompletions: COMPLETED_VIEWS_UNREPORTED_NOTE,
    ctr: "Clicks not reported for this platform",
    cpc: "Client-billed cost per click not reported",
    cpm: "Client-billed cost per thousand not reported",
    /* Names the missing FACT, and says nothing about scope: this reading IS
       scoped to the platform, the count is simply absent from its row. */
    outcome: "Not reported for this platform",
  };

  return metrics.map((metric) => {
    /*
     * `?? null` and not `|| null`: a genuinely reported 0 — zero clicks, a 0%
     * CTR, an outcome count of 0 — is an observation and must render as the
     * number, not collapse into the unavailable mark beside a note claiming it
     * was never reported.
     */
    const value = values[metric.key] ?? null;
    const efficiencyEnvelope =
      metric.key === "cpqv" ? cpqvMetric : metric.key === "cpcv" ? cpcvMetric : null;
    /* The outcome card's NAME is the backend's governed ruling, so it
       overrides the registry's fail-closed default. Every other card is named
       by the registry, which is what keeps the focused strip and the dedicated
       page reading from one vocabulary. */
    const isOutcome = metric.key === "outcome";
    return {
      key: metric.key,
      label: isOutcome ? outcomePresentation.label : metric.label,
      unit: metric.unit,
      value,
      display: formatPlatformMetricValue(metric.unit, value, {
        compact: metric.compact !== false,
      }),
      outcomeClassification: isOutcome ? outcomePresentation.classification : null,
      /* CPCV carries the exact figure in its help text, so this tile and the
         Creative Performance table below it reconcile despite rounding to the
         cent. Every other card uses the registry's own definition. */
      definition: efficiencyEnvelope
        ? efficiencyEnvelope.formula ||
          (metric.key === "cpcv"
            ? describeCostPerCompletedView(efficiencyEnvelope.value)
            : metric.definition || null)
        : isOutcome
        ? outcomePresentation.definition
        : metric.key === "cpc"
        ? describeCost(cpc, clicks, "clicks") || metric.definition || null
        : metric.key === "cpm"
        ? describeCost(cpm, impressions, "impressions", " × 1,000") ||
          metric.definition ||
          null
        : metric.key === "dataFreshness"
        ? null
        : metric.definition || null,
      ...(efficiencyEnvelope
        ? {
            spendBasis: efficiencyEnvelope.spendBasis,
            spendBasisLabel: efficiencyEnvelope.spendBasisLabel,
            numerator: efficiencyEnvelope.numerator,
            denominator: efficiencyEnvelope.denominator,
          }
        : {}),
      note:
        metric.key === "dataFreshness"
          ? null
          : value == null
          ? unavailableNotes[metric.key] || "Not reported for this window"
          : null,
    };
  });
};

/*
 * The ALL PLATFORMS strip for a campaign that delivers on exactly one
 * platform.
 *
 * WHY THIS IS THE SAME BUILDER AS THE DRILL-DOWN, CALLED WITH A DIFFERENT
 * QUESTION
 *
 * It is not a third strip. `buildFocusedPlatformKpiStrip` above already turns
 * one canonical platform row into that platform's registry KPIs, and on a
 * one-platform campaign the campaign's aggregate IS that row — same window,
 * same spend, same counts, summed over a set of one. So the aggregate can be
 * described in the platform's own vocabulary by handing the very same row to
 * the very same builder. Nothing is re-scoped and nothing is re-derived; the
 * only thing that changed is which of two vocabularies the strip speaks.
 *
 * That equality is also why this is safe to render while the page is still in
 * ALL PLATFORMS. The strip is not quietly showing a focused platform's
 * figures under a campaign heading — for this campaign the two are the same
 * numbers, and the scope, the selector and the reset behaviour are untouched.
 *
 * The CPQV and CPCV envelopes are resolved for the platform rather than for
 * the campaign, exactly as the focused rebuild resolves them, so the numerator
 * and denominator behind each cost card come from one platform's evidence in
 * one window. On a one-platform campaign those fold to the campaign's own
 * figures; asking the platform question keeps this honest if the population
 * ever gains a second row while a stale strip is still on screen.
 *
 * Returns null for every campaign this does not describe: two or more
 * reportable platforms, none at all, or a sole platform that has declared no
 * KPI profile of its own. Null is the signal to render the generic campaign
 * strip exactly as before.
 */
export const buildCampaignPlatformKpiStrip = ({
  platforms = [],
  spendEfficiency = null,
  outcomeSemantics = null,
  now = Date.now(),
} = {}) => {
  const scope = resolveCampaignKpiScope(platforms);
  if (!campaignKpiScopeIsSinglePlatform(scope)) return null;
  const row = platforms.find((entry) => entry?.key === scope.platformKey);
  if (!row) return null;
  return buildFocusedPlatformKpiStrip(row, {
    now,
    cpqvMetric: resolveSpendEfficiencyMetric(spendEfficiency, {
      platformKey: row.key,
      metricKey: "cpqv",
    }),
    cpcvMetric: resolveSpendEfficiencyMetric(spendEfficiency, {
      platformKey: row.key,
      metricKey: "cpcv",
    }),
    outcomeSemantics,
  });
};

/**
 * Platform-focused presentation of an already-built model — the tile "solo"
 * interaction. Pure selection over values the model already carries: the
 * focused view re-scopes spend / impressions / clicks and freshness to the
 * platform's own canonical row, and DECLARES everything that is reported
 * only as a window aggregate (conversions, prior-period deltas) unscoped
 * rather than pretending the whole-window figure belongs to one platform. An
 * unknown key returns the model unchanged, so a stale focus can never
 * fabricate an empty page.
 *
 * Video views are the one figure that can go either way, and the difference
 * matters for the rate derived from them. When the canonical payload breaks
 * video views out for THIS platform they are scoped like any other row value
 * and Video View Rate is real. When it reports them only for the window, they
 * stay null here — the whole-window figure is never re-attributed to one
 * platform, and no rate is minted from a numerator that belongs to a wider
 * scope than its denominator.
 */
export const focusPlatformAnalyticsModel = (model, platformKey) => {
  if (!model || !platformKey) return model;
  const row = model.platforms.find((entry) => entry.key === platformKey);
  if (!row) return model;
  const focusedVideoViews = row.videoViews != null && row.videoViews > 0 ? row.videoViews : null;
  const totals = {
    spend: row.spend ?? null,
    impressions: row.impressions ?? null,
    clicks: row.clicks ?? null,
  };
  // The focused strip states only the rates THIS platform is eligible for —
  // a CTV solo view shows its video view rate and the canonical `—` for CTR,
  // never the meaningless 0% its zero-filled click field would compute.
  const rateBasis = buildEligibleAnalyticsRateBasis([row]);
  const cpqvMetric = resolveSpendEfficiencyMetric(model.spendEfficiency, {
    platformKey: row.key,
    metricKey: "cpqv",
  });
  const cpcvMetric = resolveSpendEfficiencyMetric(model.spendEfficiency, {
    platformKey: row.key,
    metricKey: "cpcv",
  });
  return {
    ...model,
    focusedPlatform: row,
    totals,
    // The KPI strip's deltas are spend/impressions/clicks movements computed
    // on the WHOLE reporting scope, so they are dropped rather than reprinted
    // beside one platform's counts. (The contribution card's own direction is
    // a different figure: this platform's rate against its own prior row.)
    deltas: {},
    rates: {
      ctr: rateBasis.ctr.value,
      videoViewRate: rateBasis.vvr.value,
    },
    rateBasis,
    videoViews: focusedVideoViews,
    /*
     * The platform's OWN outcome count, not the campaign's. Null where this
     * platform reported none — never the whole-window total standing in for a
     * platform that did not contribute to it.
     */
    conversions: row.conversions ?? null,
    // The outcome slot, resolved for THIS platform. On video-first inventory
    // it becomes a real platform-scoped CPCV; everywhere else it keeps the
    // conversions tile the strip already had — under the SAME governed name
    // the whole-window model resolved, because focusing changes scope, not
    // what the number is called.
    outcomeKpi: buildOutcomeKpi({
      rows: [row],
      scopeKey: row.key,
      focused: true,
      outcomeSemantics: model.outcomeSemantics || null,
      canonicalMetric: cpcvMetric,
    }),
    /*
     * CPQV, re-folded over THIS platform's row alone — the same engine call
     * the campaign figure comes from, handed a one-entry observation set, so
     * numerator and denominator are this platform's spend and this platform's
     * qualified views from the same window by construction. This is a
     * different question from the campaign's CPQV, not a re-attribution of
     * it: a platform with no governed qualified signal, or whose signal
     * reported nothing, yields an unavailable basis here and the card says
     * which fact is missing. Nothing is ever inferred from an unrelated
     * count — the engine's no-fallback rule is the whole reason this can be
     * stated under focus at all.
     */
    costPerQualifiedVideoView: cpqvMetric,
    /*
     * The platform's own KPI strip, when it declares one. Null for every
     * channel that does not, so those focused views render exactly the strip
     * they always did.
     *
     * The campaign's outcome-semantics declaration is threaded through because
     * the strip's outcome card carries a governed NAME. Without it the focused
     * Display strip would fall back to the generic "Conversions" while the
     * unfocused strip on the same page, from the same payload, said
     * "Ad-Attributed Site Visits" — one payload, two names, decided by which
     * tile the reader happened to click.
     */
    focusedKpiStrip: buildFocusedPlatformKpiStrip(row, {
      cpqvMetric,
      cpcvMetric,
      outcomeSemantics: model.outcomeSemantics || null,
    }),
    aggregateOnlyNote: AGGREGATE_ONLY_NOTE,
    freshnessAt: row.lastFetchedAt || null,
    dataFreshness: row.dataFreshness || null,
    oldestPlatformLabel: null,
  };
};

/*
 * Money on a chart axis: whole dollars when the tick lands on one, cents only
 * when the scale is fine enough to need them. A tick is a scale mark, not a
 * reported total, so "$5,000" rather than "$5,000.00".
 */
const currencyAxis = (value) =>
  `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  })}`;

/**
 * A reported figure, stated in full.
 *
 * This used to compact: $87.1K, 28.1M, 1.1M. Every card on the strip, every
 * rail card's spend and every chart reading went through it, so the one
 * number a client came to the report for was the one number the report
 * would not say. Compaction is a display shortcut and not a fact about the
 * data; the face now carries the same value the API returned, with thousands
 * separators — money to the cent, counts as whole numbers.
 *
 * `options.axis` marks a chart axis tick, where money drops cents it does
 * not need. Nothing else reads the options; the chart renderer's `compact`
 * hint is accepted and ignored.
 */
export const formatAnalyticsValue = (value, kind = "count", options = {}) => {
  if (!Number.isFinite(value)) return "—";
  if (kind === "currency") {
    if (options.axis) return currencyAxis(value);
    return `$${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

/**
 * A rate, as a percentage. `—` is the canonical unavailable mark and the ONLY
 * thing an absent rate may render as: 0% is a claim, and an unreported or
 * un-divisible figure has made no claim.
 *
 * Precision follows the magnitude rather than a fixed number of places, so a
 * CTR reads 0.05% and a view rate reads 36.9% without either printing digits
 * the underlying counts do not support. A value too small to survive two
 * decimals renders as `<0.01%` — small is not the same as none.
 *
 * The rounding is half-up on the decimal value — see `roundHalfUp`. `toFixed`
 * alone used to decide these boundaries on the binary double, so a rate that
 * is exactly half a hundredth could round either way depending on bits the
 * reader cannot see.
 */
export const formatRatePercent = (value) => {
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value === 0) return "0%";
  if (value < 0.01) return "<0.01%";
  const places = value >= 10 ? 1 : 2;
  return `${roundHalfUp(value, places).toFixed(places)}%`;
};

export const formatDelta = (delta) => {
  if (!Number.isFinite(delta)) return null;
  const rounded = Math.round(delta * 10) / 10;
  return `${rounded > 0 ? "↑ +" : rounded < 0 ? "↓ " : ""}${rounded}% vs prior`;
};

/**
 * A platform row, expressed as the canonical contribution card's inputs.
 *
 * Both Cortex surfaces — the authenticated page and the public report — render
 * their rail through this one function, so neither can quietly grow its own
 * idea of what a card says. It only ever SELECTS and FORMATS what the model
 * already derived; there is no arithmetic here that could disagree with the
 * KPI strip above the rail.
 *
 * WHICH RATE THE CARD STATES
 *
 * Not CTR by assumption. The card used to hard-code the label "CTR" and print
 * clicks-over-impressions for every platform, so a CTV row — bought on
 * completed viewing, with an ad that is frequently not clickable at all —
 * reported a true, canonical, entirely meaningless `0% CTR` beside $1.5K of
 * delivery. That number is computable and product-wrong: it invites a
 * conclusion the data never supported.
 *
 * The metric is now chosen by `resolvePlatformEfficiencyMetric`, the same one
 * implementation the welcome Platform Performance legend and tooltip already
 * use, so the card and the chart cannot disagree about what a platform is read
 * on. CTV and YouTube are read on video view rate; click inventory is read on
 * CTR; a video-first platform with no video evidence states NO rate rather
 * than falling back to the 0% this exists to stop printing.
 *
 * The unavailable states are deliberate and structural:
 *
 *   - No honest rate for this platform's evidence ⇒ no label and the canonical
 *     `—`, never 0% and never a metric name the number does not answer to.
 *   - No comparable prior row ON THE SAME METRIC ⇒ `efficiencyDeltaPts` is null
 *     ⇒ no direction arrow at all, rather than a flat one implying a
 *     comparison happened.
 *   - No per-platform fetch stamp ⇒ freshness is "unknown" ⇒ the status slot
 *     stays empty. A public report is not told whether a connector is live —
 *     provenance is outside its projection — so it never claims ✓ Current on
 *     anything but a stamp it was actually given, inside the canonical window.
 */
export const buildPlatformCardPresentation = (row, { now = Date.now() } = {}) => {
  const freshness = resolvePlatformFreshness({ lastFetchedAt: row?.lastFetchedAt, now });
  const efficiency = resolvePlatformEfficiencyMetric({
    platformKey: row?.key,
    ...readPlatformCounts(row || {}),
  });
  return {
    platformKey: row?.key,
    label: row?.label,
    share: Number.isFinite(row?.spendShare) ? row.spendShare : null,
    spendText: formatAnalyticsValue(row?.spend, "currency"),
    efficiencyLabel: efficiency?.label || null,
    efficiencyText: formatRatePercent(efficiency ? efficiency.value : null),
    trend: resolveTrendDirection(row?.efficiencyDeltaPts),
    status: {
      state: freshness.state,
      detail: formatFreshnessMinutes(freshness.minutes),
    },
  };
};

export const formatFreshness = (iso, now = Date.now()) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const hours = Math.max(0, Math.round((now - then) / 3600000));
  if (hours < 1) return "Under 1h ago";
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/*
 * ---------------------------------------------------------------------------
 * THE CAMPAIGN KPI STRIP — one ordered contract, every surface.
 *
 * This exists because the strip lost a card without anyone deciding to remove
 * one. The qualified-video migration replaced a Video Views + Video View Rate
 * PAIR with a single Qualified Video Views tile — correctly, since a
 * YouTube-only rate beside a cross-platform total is the adjacency defect that
 * migration was built to end — and the eight-column grid quietly became seven
 * occupied columns. Nothing was broken enough to notice: each surface owned
 * its own copy of the strip as inline JSX, so "how many cards is this strip"
 * was not a fact anywhere in the codebase, and therefore not a fact any test
 * could hold. CL #302 had already paid for this once, when the public report
 * went a full deploy cycle still rendering the pre-migration markup.
 *
 * So the ORDER and the MEMBERSHIP are declared here, once, and both Cortex
 * surfaces render whatever this returns. A card can still be added or removed
 * — but it is now an edit to a named contract with a test on it, rather than
 * a JSX block drifting in one of two files.
 *
 * WHY THE FIFTH SLOT IS A COST
 *
 * The strip states money, delivery, response, and outcome. After the migration
 * its only cost reading was CPC, which is scoped to click-eligible inventory
 * by construction — CTV cannot appear in it, and on a video flight CTV is
 * routinely half the spend. The strip was headlining a qualified video total
 * it had no way to price. `costPerQualifiedVideoView` prices exactly that
 * total, over exactly the platforms that produced it.
 * ---------------------------------------------------------------------------
 */
export const CAMPAIGN_KPI_STRIP_ORDER = Object.freeze([
  "spend",
  "impressions",
  "clicks",
  "ctr",
  "costPerQualifiedVideoView",
  "video",
  "outcome",
  "dataFreshness",
]);

/*
 * Two of the eight are SLOTS rather than fixed metrics, and both resolve from
 * the model rather than from the surface.
 *
 * `outcome` has always worked this way — Conversions, or CPCV on inventory
 * bought on completed viewing. `video` now does too: the campaign's qualified
 * video where any platform has a governed qualified signal, and the generic
 * governed Video Views count where none does.
 *
 * That fallback is not a retreat from the qualified migration. The defect that
 * migration fixed was a YouTube-only count standing as the whole campaign's
 * video story WHILE CTV had qualified delivery to report. A Meta or TikTok
 * flight has no qualified signal because nobody has ruled on one yet, and
 * printing "—" at it would state that the campaign delivered no video when it
 * plainly did — replacing a wrong number with a wrong absence. What never
 * comes back into this slot's neighbourhood is the video RATE: a rate beside
 * the count is the adjacency the migration removed, and it stays removed in
 * both resolutions of the slot.
 */
export const CAMPAIGN_KPI_VIDEO_SLOT_KEY = "video";
export const CAMPAIGN_KPI_OUTCOME_SLOT_KEY = "outcome";

/**
 * The campaign strip's cards, in canonical order.
 *
 * `view` is the scoped model (focused or not) and `model` the unscoped one —
 * the qualified-video cards read the campaign, not the focus, which is why
 * both are taken. A FOCUSED platform is a different question with a different
 * answer and does not come through here at all; it renders `view.focusedKpiStrip`
 * from the platform's own registry profile.
 *
 * Every card is returned every time. There is no filtering step, deliberately:
 * a card whose metric this window cannot state renders its governed
 * unavailable treatment and keeps its column, because a strip that drops cards
 * is how this one silently became seven.
 */
export const buildCampaignKpiStrip = ({
  view,
  model,
  selectedMetricKey = null,
  now = Date.now(),
} = {}) => {
  const scoped = view || model || {};
  const campaign = model || view || {};

  /*
   * A platform can be focused and still land here: a platform with no KPI
   * profile of its own (Display) has no focused strip to render, so it falls
   * through to this one with its counts scoped and the campaign-level figures
   * beside them.
   *
   * The two qualified-video cards must not state the CAMPAIGN's video while
   * the cards beside them state one platform's delivery — that is the same
   * scope-mismatch reading the qualified migration exists to prevent, pointed
   * the other way. The video COUNT resolves that by falling to the generic
   * governed count for the current scope. The CPQV card resolves it the
   * stronger way: the focused model re-folds the basis over the focused
   * platform's own row, so the card states a real platform-scoped cost from
   * the same evidence as every card beside it, and abstains — naming the
   * missing qualified views — only when that platform cannot state one.
   */
  const focused = Boolean(scoped.focusedPlatform);
  const unscopedNote = scoped.aggregateOnlyNote || AGGREGATE_ONLY_NOTE;

  const cards = [
    ...PLATFORM_ANALYTICS_METRICS.map((metric) => {
      const value = scoped.totals?.[metric.key];
      const delta = scoped.deltas?.[metric.key];
      return {
        key: metric.key,
        label: metric.label,
        value: formatAnalyticsValue(value, metric.kind),
        /* The three additive window totals are the delivery chart's columns,
           so these are the only cards that select a series. */
        selectable: true,
        active: metric.key === selectedMetricKey,
        delta: formatDelta(delta),
        deltaDirection: Number.isFinite(delta) ? (delta >= 0 ? "up" : "down") : null,
        detail: null,
        tooltip: null,
      };
    }),
    {
      key: "ctr",
      label: "CTR",
      /* Derived from the counts sitting beside it in this same strip, so the
         strip reconciles against itself. Not selectable: the chart plots
         additive window totals, and a rate is not a quantity that sums. */
      value: formatRatePercent(scoped.rates?.ctr),
      selectable: false,
      active: false,
      delta: null,
      deltaDirection: null,
      detail: null,
      tooltip: null,
    },
    (() => {
      /*
       * Unfocused, the tile prices the campaign's qualified video and its
       * detail names the platforms in the numerator. FOCUSED, the model has
       * already re-folded the basis over the focused platform's row alone, so
       * the card can state a real platform-scoped cost — YouTube's spend over
       * YouTube's TrueView views, CTV's spend over CTV's completed views —
       * instead of abstaining behind the unscoped note. The scope statement
       * moves off the face (a focused strip is all one platform, so "YouTube
       * spend" would say nothing) and the face instead expands the acronym,
       * exactly as the CPCV outcome card beside it does; the arithmetic and
       * scope stay in the tooltip.
       *
       * When the focused platform cannot state the metric — no governed
       * qualified signal, or a signal whose count is absent or zero — the
       * card names the missing fact. Deliberately NOT `unscopedNote`: this
       * reading IS scoped, the qualified views are simply not reported, and
       * a cost per qualified view is never minted from video views,
       * engagements, impressions or completions standing in for them.
       */
      const tile = buildCostPerQualifiedVideoViewTile(
        focused ? scoped.costPerQualifiedVideoView : campaign.costPerQualifiedVideoView
      );
      if (!focused) return tile;
      return {
        ...tile,
        selectable: false,
        active: false,
        delta: null,
        deltaDirection: null,
        detail: tile.available
          ? [COST_PER_QUALIFIED_VIDEO_VIEW_LONG_LABEL, tile.spendBasisLabel]
              .filter(Boolean)
              .join(" · ")
          : QUALIFIED_VIEWS_UNREPORTED_NOTE,
      };
    })(),
    (() => {
      const tiles = focused ? null : buildQualifiedVideoKpiTiles(campaign.qualifiedVideo);
      if (tiles) {
        return {
          key: CAMPAIGN_KPI_VIDEO_SLOT_KEY,
          metricKey: "qualifiedVideoViews",
          label: tiles.count.label,
          value: tiles.count.value,
          selectable: false,
          active: false,
          delta: null,
          deltaDirection: null,
          /* "CTV 147.4K · 98.30% VCR · YouTube 29.3K · 25.06% VVR" — the
             native rates, each bound to the platform that measured it. This
             is the one place they belong, and they do not return to the
             strip as a card of their own. */
          detail: tiles.count.detail,
          tooltip: tiles.count.tooltip,
        };
      }
      /*
       * Either no platform on this campaign has a governed qualified signal,
       * or a platform is focused and the campaign's total does not describe
       * it. Both resolve to the generic governed count for the current scope,
       * which under focus is a whole-window figure and says so.
       */
      return {
        key: CAMPAIGN_KPI_VIDEO_SLOT_KEY,
        metricKey: "videoViews",
        label: "Video Views",
        value:
          scoped.videoViews != null
            ? formatAnalyticsValue(scoped.videoViews)
            : focused
            ? "—"
            : "Not reported",
        selectable: false,
        active: false,
        delta: null,
        deltaDirection: null,
        detail: focused && scoped.videoViews == null ? unscopedNote : null,
        tooltip: null,
      };
    })(),
    {
      key: "outcome",
      /* Which metric this is remains the model's ruling, not the strip's:
         video-first inventory reads on CPCV, everything else on the
         conversions figure under its governed name — "Ad-Attributed Site
         Visits" only when the backend verified the tracker configuration,
         the generic "Conversions" otherwise. */
      label: scoped.outcomeKpi?.label || "Conversions",
      value: scoped.outcomeKpi?.display ?? "—",
      selectable: false,
      active: false,
      delta: null,
      deltaDirection: null,
      detail: scoped.outcomeKpi?.note || null,
      tooltip: scoped.outcomeKpi?.definition || null,
      metricKey: scoped.outcomeKpi?.metricKey || null,
      outcomeClassification: scoped.outcomeKpi?.outcomeClassification || null,
    },
    {
      key: "dataFreshness",
      label: "Data Freshness",
      value: formatFreshness(scoped.freshnessAt, now) || "—",
      selectable: false,
      active: false,
      delta: null,
      deltaDirection: null,
      detail: null,
      tooltip: null,
    },
  ];

  /*
   * The order is asserted, not assumed. Building the array in the right
   * sequence is what actually orders the strip; this catches the case where a
   * future edit inserts or renames a card and the declared contract above
   * stops describing what ships.
   */
  const keys = cards.map((card) => card.key);
  if (keys.join("|") !== CAMPAIGN_KPI_STRIP_ORDER.join("|")) {
    throw new Error(
      `Campaign KPI strip drifted from its canonical order: got ${keys.join(", ")}`
    );
  }

  return cards;
};

/*
 * ---------------------------------------------------------------------------
 * The governed daily series → the canonical time-axis Delivery chart.
 *
 * `canonicalReporting.dailyByChannel` is the daily reporting contract: one
 * entry per reported DAY, each carrying the same per-channel metrics shape as
 * the window's `byChannel`, built by the backend from the same resolved
 * metrics as the window map — so the days reconcile with the window totals by
 * construction. When it is present, both Cortex surfaces draw delivery BY DAY
 * through the SAME shared time-series builder Quantum's page uses; when it is
 * absent, they keep the platform-axis window-totals state. Nothing here
 * fabricates a date: days nobody reported are absent, never zero, and the
 * flight's remaining days are not drawn at all.
 * ---------------------------------------------------------------------------
 */

export const summaryDailyByChannel = (summary) => {
  const reporting = summary?.canonicalReporting || summary?.canonicalSummary || summary || {};
  const daily = reporting.dailyByChannel;
  return daily && typeof daily === "object" && Array.isArray(daily.days) && daily.days.length
    ? daily
    : null;
};

const formatChartDayLabel = (value) => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime())
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
      }).format(parsed)
    : String(value);
};

/**
 * Each reported day, folded to platform rows with the SAME identity rules the
 * window model applies: brand-key resolution, the measurement-only exclusion,
 * and the Meta family folding into one governed Meta platform when the
 * canonical rollup is present. A day whose entries carry no positive
 * spend/impressions/clicks contributes nothing, and a payload with no dated
 * evidence returns null — the signal to keep the window-totals state.
 */
export const buildPlatformAnalyticsDailyRows = (summary) => {
  const daily = summaryDailyByChannel(summary);
  if (!daily) return null;
  const reporting = summary?.canonicalReporting || summary?.canonicalSummary || summary || {};
  const metaRollup =
    reporting.metaRollup && typeof reporting.metaRollup === "object"
      ? reporting.metaRollup
      : null;

  const perDay = daily.days
    .map((day) => {
      const rows = new Map();
      const unavailableSpend = new Set();
      const unavailableImpressions = new Set();
      const unavailableClicks = new Set();
      Object.entries(day?.byChannel || {}).forEach(([channelKey, entry]) => {
        let brandKey =
          resolvePlatformBrandKey(entry?.platform || entry?.channel || channelKey) ||
          String(channelKey || "").toLowerCase();
        if (MEASUREMENT_ONLY_KEYS.has(String(channelKey).toLowerCase())) return;
        if (MEASUREMENT_ONLY_KEYS.has(String(brandKey).toLowerCase())) return;
        // The window model folds the Meta family through the rollup's own
        // totals; a day has no rollup totals, so the fold sums the family's
        // dated entries under the same governed Meta identity instead.
        if (metaRollup && META_FAMILY_BRAND_KEYS.has(String(brandKey).toLowerCase())) {
          brandKey = "meta";
        }
        const metrics = entry?.metrics || entry || {};
        const row = rows.get(brandKey) || {
          key: brandKey,
          spend: 0,
          impressions: 0,
          clicks: 0,
          videoViews: null,
          trueViewViews: null,
          /* The completion evidence a video platform is actually read on.
             CTV reports no view, so without these the dated tooltip named the
             platform and stated nothing beside it. */
          videoStarts: null,
          videoCompletions: null,
          completionRate: null,
          completionRateChannels: 0,
          hasAny: false,
        };
        for (const metricKey of ["spend", "impressions", "clicks"]) {
          const value = pickMetric(metrics, metricKey);
          if (metricKey === "spend" && value == null) unavailableSpend.add(brandKey);
          if (metricKey === "impressions" && value == null) unavailableImpressions.add(brandKey);
          if (metricKey === "clicks" && value == null) unavailableClicks.add(brandKey);
          if (value != null && value > 0) {
            row[metricKey] += value;
            row.hasAny = true;
          }
        }
        const rowTrueViewViews = pickTrueViewViews(metrics);
        const rowVideoViews = rowTrueViewViews ?? pickVideoViews(metrics);
        if (rowVideoViews != null) {
          row.videoViews = (row.videoViews ?? 0) + rowVideoViews;
          if (brandKey === "youtube") {
            row.trueViewViews = (row.trueViewViews ?? 0) + rowVideoViews;
          }
        }
        const rowVideoStarts = pickVideoStarts(metrics);
        if (rowVideoStarts != null) {
          row.videoStarts = (row.videoStarts ?? 0) + rowVideoStarts;
        }
        const rowVideoCompletions = pickVideoCompletions(metrics);
        if (rowVideoCompletions != null) {
          row.videoCompletions = (row.videoCompletions ?? 0) + rowVideoCompletions;
        }
        /* A rate is not additive: the last stated one is kept with a count of
           how many channels stated it, and the governed resolver discards it
           the moment that count exceeds one. */
        const rowCompletionRate = pickCompletionRate(metrics);
        if (rowCompletionRate != null) {
          row.completionRate = rowCompletionRate;
          row.completionRateChannels += 1;
        }
        rows.set(brandKey, row);
      });
      unavailableSpend.forEach((key) => { rows.get(key).spend = null; });
      unavailableImpressions.forEach((key) => { rows.get(key).impressions = null; });
      unavailableClicks.forEach((key) => { rows.get(key).clicks = null; });
      return { date: String(day?.date || "").slice(0, 10), rows };
    })
    .filter(
      (day) =>
        isIsoDayString(day.date) &&
        Array.from(day.rows.values()).some((row) => row.hasAny)
    );

  if (!perDay.length) return null;
  return {
    perDay,
    observedDays: perDay.length,
    complete: daily.complete !== false,
  };
};

/**
 * The dated Delivery chart model for the Platform Analytics surfaces, or null
 * when the payload carries no daily evidence — the caller's signal to build
 * the platform-axis window-totals model instead.
 *
 * The stack order is the window model's platform order, so the dated chart
 * and the rail beneath it name platforms in the same sequence. Each column's
 * tooltip carries that DAY's governed per-platform efficiency reading — a CTV
 * segment states its video view rate, a click-bought segment its CTR — from
 * the one shared efficiency resolver, already formatted by the one shared
 * rate formatter. There is no chart-wide efficiency series: the platforms in
 * the stack are read on different metrics, and one line across them would
 * blend definitions that share no unit.
 *
 * OBSERVED SITE VISITS RIDE THE SAME DATE AXIS.
 *
 * GA4 used to be a second chart below this one, which asked a reader to
 * compare two pictures by eye and made "did traffic move with delivery" a
 * question about scrolling. It is the same dates, so it belongs on the same
 * axis — as an overlay on the canonical chart's own right-hand scale, which
 * the shared renderer already draws for Quantum's daily view.
 *
 * Visits and spend share no unit, so the overlay never touches the spend
 * scale: forcing them together would either flatten the line into the
 * baseline or invent a relationship by making the curves track. The right
 * axis is what keeps this a correlation view rather than an implied
 * equivalence, and nothing here claims the media caused the traffic.
 *
 * The columns stay the DELIVERY days. A GA4 observation is attached to a day
 * this chart already draws; a day GA4 reported and media did not is counted
 * and declared in the description rather than given a column of its own,
 * because inventing an empty delivery column to host a traffic point would
 * state a reporting day that delivery never had. Dates are matched as the
 * canonical strings both series are keyed by, in GA4's own time zone — no
 * re-bucketing, no zero fill, and a day GA4 did not report stays a hole.
 */
export const buildDailyDeliveryChartModel = ({ summary, model, metric }) => {
  if (!model || !Array.isArray(model.platforms) || !model.platforms.length) return null;
  const daily = buildPlatformAnalyticsDailyRows(summary);
  if (!daily) return null;

  const metricKey = metric?.key || "spend";
  const metricLabel = metric?.label || "Spend";
  const metricKind = metric?.kind || "currency";

  const platformKeys = model.platforms
    .map((row) => row.key)
    .filter((key) =>
      daily.perDay.some((day) => {
        const row = day.rows.get(key);
        return row && Number.isFinite(row[metricKey]) && row[metricKey] > 0;
      })
    );
  if (!platformKeys.length) return null;

  const platformsByKey = new Map(
    model.platforms.map((row) => [
      row.key,
      {
        key: row.key,
        label: row.label,
        color: resolvePlatformBranding(row.key)?.color || null,
      },
    ])
  );

  /*
   * The observed GA4 series, keyed by its own canonical date string. A reported
   * 0 is an observation and stays; a day absent from the payload is absent
   * here, which is what leaves the hole in the overlay instead of a zero.
   *
   * Located through the shared resolver rather than read off a property here.
   * This adapter serves TWO envelopes — the public report publishes the series
   * at the payload root, the authenticated `/performance-summary` attaches it
   * to the canonical summary — and reading only the root is exactly how the
   * authenticated Platform Analytics page lost its overlay while the public
   * report kept drawing one from the same series.
   */
  const {
    byDate: siteVisitsByDate,
    timeZone: siteVisitsTimeZone,
    label: siteVisitsLabel = "Site Visits · GA4",
    metric: siteVisitsMetric,
  } =
    indexObservedSiteVisitsByDate(summary);
  const siteVisitsAreAttributed = siteVisitsMetric === "ad_attributed_site_visits";
  const deliveryDates = new Set(daily.perDay.map((day) => String(day.date)));
  const visitsOffAxis = [...siteVisitsByDate.keys()].filter(
    (date) => !deliveryDates.has(date)
  ).length;

  const buckets = daily.perDay.map((day) => ({
    key: day.date,
    label: formatChartDayLabel(day.date),
    byPlatform: Object.fromEntries(
      platformKeys
        .map((key) => {
          const row = day.rows.get(key);
          const value = row?.[metricKey];
          return Number.isFinite(value) && value > 0 ? [key, { [metricKey]: value }] : null;
        })
        .filter(Boolean)
    ),
    planMedia: 0,
    siteVisits: siteVisitsByDate.has(String(day.date))
      ? siteVisitsByDate.get(String(day.date))
      : /* Not zero: an unreported day is a hole in the line. */ null,
    extraRows: platformKeys
      .map((key) => {
        const row = day.rows.get(key);
        if (!row || !row.hasAny) return null;
        const resolved = resolvePlatformEfficiencyMetric({
          platformKey: key,
          ...readPlatformCounts(row),
        });
        if (!resolved) return null;
        const label = platformsByKey.get(key)?.label || key;
        return { label: `${label} ${resolved.label}`, value: formatRatePercent(resolved.value) };
      })
      .filter(Boolean),
  }));

  const plottedVisits = buckets.filter((bucket) => bucket.siteVisits != null);
  const siteVisitsAvailable = plottedVisits.length > 0;

  const spanLabel =
    buckets.length === 1
      ? buckets[0].label
      : `${buckets[0].label}–${buckets[buckets.length - 1].label}`;

  /*
   * What the overlay adds to the spoken description. It has to name the second
   * axis, say which days were observed, and be explicit that the rest are
   * unreported rather than zero — a reader who cannot see the gap in the line
   * has only this sentence to tell them the difference.
   */
  const visitsSummary = !siteVisitsAvailable
    ? ""
    : [
        ` ${siteVisitsLabel}${
          siteVisitsTimeZone ? `, dates in ${siteVisitsTimeZone}` : ""
        } is overlaid on a secondary right-hand axis with its own scale, not on the ${String(
          metricLabel
        ).toLowerCase()} scale: `,
        plottedVisits
          .map(
            (bucket) =>
              // Spoken in full. "1.7K" is a glance format; a description read
              // aloud is where the exact observation belongs.
              `${bucket.label} ${bucket.siteVisits.toLocaleString("en-US", {
                maximumFractionDigits: 0,
              })}`
          )
          .join(", "),
        `. ${plottedVisits.length} of ${buckets.length} reporting day${
          buckets.length === 1 ? "" : "s"
        } carr${plottedVisits.length === 1 ? "ies" : "y"} ${
          siteVisitsAreAttributed ? "an attributed observation" : "a GA4 observation"
        }; `,
        "any other day is unreported rather than zero, and the line breaks there instead of being drawn through it.",
        visitsOffAxis
          ? ` ${visitsOffAxis} ${siteVisitsAreAttributed ? "site-visit" : "GA4"} observation${
              visitsOffAxis === 1 ? "" : "s"
            } fall on dates with no reported delivery, so they have no column on this chart.`
          : "",
        siteVisitsAreAttributed
          ? " Attribution is backed by the selected campaign's verified site-wide conversion tracker."
          : " Correlation only: this states traffic observed beside delivery and does not claim the media caused it.",
      ].join("");

  const textSummary = [
    `${metricLabel} by reporting day for ${spanLabel}, stacked by platform: `,
    buckets
      .map((bucket) => {
        const parts = platformKeys
          .map((key) => {
            const entry = bucket.byPlatform[key];
            return entry
              ? `${platformsByKey.get(key)?.label || key} ${formatAnalyticsValue(
                  entry[metricKey],
                  metricKind
                )}`
              : null;
          })
          .filter(Boolean);
        return `${bucket.label} — ${parts.join(", ")}`;
      })
      .join("; "),
    `. ${daily.observedDays} reported day${daily.observedDays === 1 ? "" : "s"}.`,
    " Days without reported delivery are left out rather than drawn as zero.",
    daily.complete
      ? ""
      : " Some delivery in this window was reported only as a window total and cannot be attributed to a day, so it is absent from these columns.",
    visitsSummary,
  ].join("");

  const chartMetrics = {
    [metricKey]: { key: metricKey, label: metricLabel, unit: metricKind },
    ctr: {
      key: "ctr",
      label: "CTR",
      unit: "percent",
      definition: "Clicks divided by impressions, for the reporting scope.",
    },
  };

  return buildTimeSeriesDeliveryChartModel({
    viewModel: {
      volumeMetricKey: metricKey,
      efficiencyMetricKey: "ctr",
      efficiencyComparable: false,
      platformKeys,
      buckets,
      /*
       * Client vocabulary only — see spendEfficiencyContract. The internal
       * label is the contract's bookkeeping, never a client legend.
       */
      spendBasisClientLabel: "Client-billed",
      /*
       * Derived from the buckets that actually carry an observation, never
       * asserted: the flag drives the legend entry and the right-hand axis, so
       * declaring it on a payload with no plotted GA4 day would name a series
       * the chart cannot draw.
       */
      siteVisits: { available: siteVisitsAvailable, label: siteVisitsLabel },
      chartTextSummary: textSummary,
    },
    resolvePlatform: (key) => platformsByKey.get(key) || null,
    resolveMetric: (key) => chartMetrics[key] || null,
    /*
     * Every reading in full — tooltip, axis and value label alike. The adapter
     * used to hand the renderer's `{ compact: false }` a full count and its
     * `{ compact: true }` an abbreviation; now there is nothing to abbreviate,
     * so the options only carry the axis hint through to the money formatter.
     */
    formatMetricValue: (unit, value, options = {}) => {
      if (unit === "percent") return formatRatePercent(value);
      return formatAnalyticsValue(value, unit, options);
    },
  });
};
